import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { styled, useForkRef } from '@mui/material';

import { MenuOptionItem } from '../MenuOptionItem';
import { DropInsertLine } from './DropInsertLine';

import type { DraggableMenuOptionItemProps } from './types';

interface DragItem {
  id: string;
  index: number;
  type: string;
}

const ITEM_TYPE = 'DRAGGABLE_MENU_OPTION_ITEM';

/**
 * 드래그 가능한 메뉴 옵션 아이템
 *
 * MenuOptionItem을 래핑하여 react-dnd 기반 드래그 앤 드롭 기능을 제공합니다.
 */
export function DraggableMenuOptionItem(props: DraggableMenuOptionItemProps) {
  const {
    ref: externalRef,
    dragId,
    index,
    draggable = true,
    isDraggingOnMenu,
    onDragStart,
    onDragEnd,
    onHover,
    onHoverEnd,
    onMove,
    showInsertLine = false,
    insertPosition = 'bottom',
    disabled,
    ...menuOptionItemProps
  } = props;

  const rootRef = useRef<HTMLLIElement>(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: () => {
      onDragStart?.(dragId);
      return { id: dragId, index, type: ITEM_TYPE } as DragItem;
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    // disabled 아이템도 드래그로 순서 변경 가능 (옵션 선택만 비활성화)
    canDrag: draggable,
    end: () => {
      onDragEnd?.();
    },
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (item: DragItem, monitor) => {
      if (!rootRef.current || !onHover || item.index === index) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // 커서 위치에 따라 삽입 위치 결정
      const hoverBoundingRect = rootRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      const insertPosition = hoverClientY < hoverMiddleY ? 'top' : 'bottom';

      onHover(dragIndex, hoverIndex, insertPosition);
    },
    drop: (item: DragItem, monitor) => {
      if (!onMove || !rootRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // 드롭 시 삽입 위치 계산
      const hoverBoundingRect = rootRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      const dropPosition = hoverClientY < hoverMiddleY ? 'top' : 'bottom';

      // 실제 목표 인덱스 계산
      let targetIndex = hoverIndex;
      if (dropPosition === 'bottom') {
        targetIndex = hoverIndex + 1;
      }

      // 드래그 아이템이 제거될 위치 보정
      if (dragIndex < targetIndex) {
        targetIndex = targetIndex - 1;
      }

      if (dragIndex !== targetIndex) {
        onMove(dragIndex, targetIndex);
      }

      onHoverEnd?.();
    },
  });

  // 드래그 핸들에 drag 연결, 아이템 전체에 drop과 preview 연결
  const dragHandleRef = useForkRef<HTMLDivElement>(element => {
    drag(element);
  });

  const combinedRef = useForkRef(rootRef, externalRef, element => {
    drop(element);
    preview(element);
  });

  return (
    <DraggableMenuOptionItemRoot isDragging={isDragging} isDraggingOnMenu={isDraggingOnMenu}>
      {showInsertLine && <DropInsertLine visible={showInsertLine} position={insertPosition} />}
      <MenuOptionItem
        {...menuOptionItemProps}
        ref={combinedRef}
        disabled={disabled}
        showDragHandle={draggable}
        dragHandleRef={dragHandleRef}
        dragHandleDisabled={!draggable}
        isDragging={isDragging}
        style={{
          // 드래그 중에는 disabled 아이템도 드롭 대상이 될 수 있도록 pointerEvents 활성화
          ...(isDraggingOnMenu ? { pointerEvents: 'auto' } : {}),
          ...menuOptionItemProps.style,
        }}
      />
    </DraggableMenuOptionItemRoot>
  );
}

DraggableMenuOptionItem.displayName = 'DraggableMenuOptionItem';

// Wrapper 컴포넌트 (position: relative로 DropInsertLine 위치 기준)
const DraggableMenuOptionItemRoot = styled('div')<{
  isDragging: boolean;
  isDraggingOnMenu: boolean;
}>`
  position: relative;
  opacity: ${({ isDragging }) => (isDragging ? 0.5 : 1)};
  ${({ isDraggingOnMenu }) => isDraggingOnMenu && 'pointer-events: auto;'}
`;
